import { ChapterEnum, WorkOrderStatusEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';

import { WorkOrderPriceTypeEnum } from '../workorderTypes';

export const usePriceGetAdditionalClassName = (
    data: any,
    chapter: ChapterEnum,
    workOrderStatus?: WorkOrderStatusEnum,
) => {
    const cellsColorMarkers = useMemo(() => {
        const { actsKC2 = [], seasons = [] } = data || {};
        const markers = {
            seasonsOdd: [],
            seasonsEven: [],
            actsOdd: [],
            actsEven: [],
        };

        seasons.forEach((season: any, index: number) => {
            const { id } = season;
            const isOddSeason = !(index % 2);
            const actsKey = isOddSeason ? 'actsOdd' : 'actsEven';

            markers[isOddSeason ? 'seasonsOdd' : 'seasonsEven'].push(id as never);
            markers[actsKey] = [
                ...markers[actsKey],
                ...actsKC2.filter((a: any) => a.seasonId === id).map((a: any) => a.id),
            ] as never;
        });

        return markers;
    }, [data]);

    return useCallback(
        ({ column, row }: any) => {
            const { accessor } = column;
            const { type } = row;
            let isEven;
            let isOdd;
            let colorMarker;

            const hasEmptySeasonVolume =
                row?.hasEmptySeasonVolume && workOrderStatus === WorkOrderStatusEnum.OnFormation;

            const alert =
                chapter !== ChapterEnum.Others &&
                !row?.isActKC2Price &&
                [
                    WorkOrderPriceTypeEnum.OTHERS,
                    WorkOrderPriceTypeEnum.OTHERS_NEW,
                    WorkOrderPriceTypeEnum.UER,
                    WorkOrderPriceTypeEnum.MTR,
                    WorkOrderPriceTypeEnum.MTR_CUSTOMER_MATERIAL,
                    WorkOrderPriceTypeEnum.MTR_CUSTOMER_EQUIPMENT,
                    WorkOrderPriceTypeEnum.MTR_CONTRACTOR_MATERIAL,
                    WorkOrderPriceTypeEnum.MTR_CONTRACTOR_EQUIPMENT,
                ].includes(row?.type) &&
                (hasEmptySeasonVolume || row?.hasEmptySeasonCost);

            if (accessor?.startsWith('season')) {
                isEven = cellsColorMarkers.seasonsEven.find((id) => accessor?.endsWith(`_${id}`));
                isOdd = cellsColorMarkers.seasonsOdd.find((id) => accessor?.endsWith(`_${id}`));
            }

            if (accessor?.startsWith('actKC2')) {
                isEven = cellsColorMarkers.actsEven.find((id) => accessor?.endsWith(`_${id}`));
                isOdd = cellsColorMarkers.actsOdd.find((id) => accessor?.endsWith(`_${id}`));
            }

            if (isEven || isOdd) {
                colorMarker = isEven ? 'even-column' : 'odd-column';
            }

            const boldKey = accessor?.toLowerCase().includes('totalplan') ? 'bold' : '';
            const alertKey = alert ? 'alert' : '';
            const headerClass = !row.nestLevel ? 'groupHeader' : '';
            const excludedClass = row.isExcludedPrice ? 'transparentRow' : '';

            return [accessor, type, colorMarker, boldKey, alertKey, headerClass, excludedClass].join(' ');
        },
        [
            cellsColorMarkers.actsEven,
            cellsColorMarkers.actsOdd,
            cellsColorMarkers.seasonsEven,
            cellsColorMarkers.seasonsOdd,
            chapter,
            workOrderStatus,
        ],
    );
};
